/**
 * Ownership / provenance defaults and helpers for custom indicator projects.
 */

import type {
  CustomIndicatorOwner,
  CustomIndicatorProject,
  CustomIndicatorSource,
} from "./types";

export const DEFAULT_OWNER: CustomIndicatorOwner = "user";
export const DEFAULT_SOURCE: CustomIndicatorSource = "created";

export function defaultUserProjectOwnership(): Pick<
  CustomIndicatorProject,
  "owner" | "source" | "readOnly" | "hasSource" | "publisher"
> {
  return {
    owner: "user",
    source: "created",
    readOnly: false,
    hasSource: true,
    publisher: undefined,
  };
}

/** Normalize legacy rows missing ownership fields. */
export function normalizeProject(
  project: CustomIndicatorProject
): CustomIndicatorProject {
  const owner = project.owner ?? DEFAULT_OWNER;
  const source = project.source ?? DEFAULT_SOURCE;
  const readOnly = project.readOnly ?? false;
  const hasSource =
    project.hasSource ?? (source !== "imported-binary" && source !== "marketplace");
  return {
    ...project,
    owner,
    source,
    readOnly,
    hasSource,
    publisher:
      project.publisher ??
      (owner === "system" ? "System" : owner === "external" ? "External" : undefined),
  };
}

export function isProjectEditable(project: CustomIndicatorProject): boolean {
  const p = normalizeProject(project);
  return p.owner === "user" && !p.readOnly && p.hasSource;
}

export function canExportSourceBundle(project: CustomIndicatorProject): boolean {
  const p = normalizeProject(project);
  return p.hasSource && !p.readOnly;
}

export function ownerDisplayLabel(owner: CustomIndicatorOwner): string {
  switch (owner) {
    case "system":
      return "System";
    case "external":
      return "External";
    default:
      return "You";
  }
}

export function sourceDisplayLabel(source: CustomIndicatorSource): string {
  switch (source) {
    case "created":
      return "Created";
    case "imported-source":
      return "Imported (source)";
    case "imported-binary":
      return "Imported (binary)";
    case "marketplace":
      return "Marketplace";
    case "system":
      return "System";
    default:
      return source;
  }
}

export function buildOwnershipTags(project: CustomIndicatorProject): string[] {
  const p = normalizeProject(project);
  const tags: string[] = [];
  tags.push(p.kind === "trading-algo" ? "algo" : "indicator");
  if (p.owner === "user") tags.push("created-by-you");
  else if (p.owner === "system") tags.push("system");
  else if (p.owner === "external") tags.push("external");
  if (p.source === "imported-source" || p.source === "imported-binary") {
    tags.push("imported");
  }
  if (!p.hasSource) tags.push("binary-only");
  else tags.push("source-available");
  if (p.readOnly) tags.push("read-only");
  if (p.overlay) tags.push("overlay");
  else tags.push("pane");
  return tags;
}
